import React, { useState } from 'react';
import { Shield, LogIn, AlertCircle, Loader2 } from 'lucide-react';
import { login, login2FA } from '../../services/authApi';
import TOTPInput from './TOTPInput';

const LoginPage = ({ onLoginSuccess, onShowRegister }) => {
  const [step, setStep] = useState('credentials'); // 'credentials' | '2fa'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [preAuthToken, setPreAuthToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCredentials = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.requires2FA) {
        setPreAuthToken(data.preAuthToken);
        setStep('2fa');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handle2FA = async (code) => {
    setError('');
    setLoading(true);
    try {
      const data = await login2FA(preAuthToken, code);
      if (data.token) {
        onLoginSuccess(data.user);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-base flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo / Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 mb-4">
            <Shield size={32} className="text-white" />
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
            Stock Monitor
          </h1>
          <p className="text-txt-secondary mt-1">Accedi al tuo portafoglio</p>
        </div>

        <div className="bg-card rounded-2xl border border-border p-6 shadow-lg">
          {step === 'credentials' ? (
            <form onSubmit={handleCredentials} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  className="w-full px-4 py-3 rounded-lg border border-border bg-surface text-txt-primary
                    focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                  placeholder="la-tua@email.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-1">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-border bg-surface text-txt-primary
                    focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                  placeholder="••••••••"
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-500 text-sm bg-red-500/10 rounded-lg p-3">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium
                  hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <LogIn size={18} />}
                {loading ? 'Accesso...' : 'Accedi'}
              </button>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="text-center">
                <Shield size={24} className="mx-auto text-blue-500 mb-2" />
                <h2 className="text-lg font-semibold text-txt-primary">Verifica 2FA</h2>
                <p className="text-txt-secondary text-sm mt-1">Inserisci il codice dalla tua app Authenticator</p>
              </div>

              <TOTPInput onComplete={handle2FA} disabled={loading} />

              {error && (
                <div className="flex items-center gap-2 text-red-500 text-sm bg-red-500/10 rounded-lg p-3">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}

              {loading && (
                <div className="flex justify-center">
                  <Loader2 size={24} className="animate-spin text-blue-500" />
                </div>
              )}

              <button
                onClick={() => { setStep('credentials'); setError(''); }}
                className="w-full py-2 text-sm text-txt-secondary hover:text-txt-primary transition-colors"
              >
                ← Torna al login
              </button>
            </div>
          )}

          {/* Registration link hidden: user creation is via CLI (scripts/create-user.js).
              To re-enable, uncomment the block below. */}
          {/* <div className="mt-4 pt-4 border-t border-border text-center">
            <button
              onClick={onShowRegister}
              className="text-sm text-blue-500 hover:text-blue-400 transition-colors"
            >
              Non hai un account? Registrati
            </button>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
